import React, { useContext } from "react";
import { AppContext } from "Context/AppContext";
import { useNavigate } from "react-router";
import { toast } from "Components/DesignSystem/Toast/useToast";
import DropdownButton from "Components/DesignSystem/DropdownButton/DropdownButton";
import { DropdownButtonOption } from "types/DesignSystem/DropdownButton";

type SearchBarCreateButtonProps = {
  id: string;
  guardNavigation?: (action: () => void) => boolean;
}

/**
 * The order the menu is read in, and the ids the option testids are built from
 * (`create-btn-option-<id>`). Kept at module scope so the array identity is stable
 * across renders and so a spec can import it if it ever needs the full list.
 */
const CREATE_OPTIONS: DropdownButtonOption[] = [
  { id: 'category-type',  label: 'Category Type' },
  { id: 'category',       label: 'Category' },
  { id: 'attribute',      label: 'Attribute' },
  { id: 'rule',           label: 'Rule' },
  { id: 'product',        label: 'Product' },
  { id: 'guide-model',    label: 'Guide Model' },
  { id: 'commend-model',  label: 'Commend Model' },
  { id: 'ab-test',        label: 'A/B Test' },
];

/**
 * The global Create menu.
 *
 * **This was a Bootstrap 5 dropdown** — `data-bs-toggle="dropdown"`, a `.dropdown-menu`
 * `<ul>`, `.dropdown-item` anchors — driven by `bootstrap.bundle.min.js`, which
 * `index.html` still loads alongside jQuery. It was one of nine such call sites in
 * `src/`; **eight remain** (counted 2026-08-30, prose mentions excluded). It is now DS
 * `DropdownButton`, which brings the control grid, the focus ring, Escape and
 * outside-click dismissal, and menu semantics with it.
 *
 * ────────────────────────────────────────────────────────────────────────────────
 * **Five of the eight items did not work before this change.** Not a regression risk —
 * a bug this migration removes, and it is worth understanding so nobody reintroduces it.
 *
 * Every item was a `<Link to="#">` with an `onClick` that navigated imperatively. React
 * Router's link handler runs the caller's `onClick` **first**, then — if the event was not
 * default-prevented — performs its own navigation to `to`. `to="#"` resolves against the
 * location captured at render, i.e. the page you were on. Both navigations happen inside
 * one event handler, so the router never re-renders between them and the second one wins.
 *
 * Guide Model, Commend Model and A/B Test called `e.preventDefault()`, so their `onClick`
 * navigation survived. **Category Type, Category, Attribute, Rule and Product did not**,
 * and were silently bounced straight back to the page the user was already on. Measured
 * against a real `MemoryRouter`: clicking any of those five from `/gms/somewhere` left the
 * location at `/gms/somewhere`, including after the categories bounce timer fired.
 *
 * There is no `<Link>` here any more, so the clobber cannot happen. Every item is pinned
 * by `__tests__/SearchBarCreateButton.test.tsx`.
 * ────────────────────────────────────────────────────────────────────────────────
 *
 * **Permission failures stay toasts, not `disabled` options.** `DropdownButtonOption`
 * takes a `disabled` flag and reaching for it is the obvious move — but today an
 * unpermitted user sees the item and is *told why* when they click it. Greying the row out
 * would drop that message and leave them guessing. If that ever changes it needs a
 * `tooltip` on the option carrying the same sentence.
 */
const SearchBarCreateButton = ({id, guardNavigation}: SearchBarCreateButtonProps) => {
    const appData = useContext<any>(AppContext)
    const navigate = useNavigate();

    /**
     * Category Type and Category differ only in the `catType` they stash and where they
     * land, so the bounce lives in one place.
     *
     * The bounce: when the user is *already* on a categories route, going straight to the
     * target would leave the mounted component in place with its existing state, so it
     * detours through `/workspace/empty` and comes back a second later to force a fresh
     * form. Preserved exactly, timer included.
     */
    const createCategory = (catType: 'C' | 'CT', target: string, what: string) => {
      if (appData.pm.checkPermission("gmscategory_update")) {
        const doNavigate = () => {
          appData.setCatType(catType);

          // The category routes are mounted at `/gms/categories/:type?/:uid?` in App.tsx;
          // the pre-merge `/gms/taxonomy/category/*` paths have no matching <Route> and
          // fall through to the error page. The bounce-via-`/workspace/empty` re-mounts
          // the same component when the user is already on it so a fresh form opens.
          if (
            window.location.pathname === "/gms/categories" ||
            window.location.pathname === "/gms/categories/c" ||
            window.location.pathname === "/gms/categories/ct"
          ) {
            navigate({ pathname: `/workspace/empty` }, { replace: true });
            setTimeout(() => {
              navigate({ pathname: target }, { replace: true });
            }, 1000);
          } else {
            navigate({ pathname: target }, { replace: true });
          }
        };
        if (guardNavigation?.(doNavigate)) return;
        doNavigate();
      } else {
        toast.info(`You don't have permission to create the ${what}`);
      }
    };

    const navigateToAttributeForm = () => {
      if (appData.pm.checkPermission("gmsattributes_update")) {
        const doNavigate = () => {
          // Path was renamed to `/gms/attributes/...` in commit c00bce46e ("Fix
          // Consistent URLs for navigation"); the merge resolution that brought in
          // guardNavigation / Commend / A/B accidentally reverted these paths to the
          // legacy `/gms/taxonomy/attribute` form, which has no matching <Route> in
          // App.tsx and falls through to the generic error page.
          if (window.location.pathname.includes("gms/attributes")) {
            appData.setEditingItem({
              form: "edit",
              id: "0",
            });
          } else {
            navigate({ pathname: `/gms/attributes/0` }, { replace: true });
          }
        };
        if (guardNavigation?.(doNavigate)) return;
        doNavigate();
      } else {
        toast.info("You don't have permission to create the attribute");
      }
    };

    const createRule = () => {
      if (appData.pm.checkPermission("gmsrules_update")) {
        const doNavigate = () => {
          navigate({ pathname: `/gms/rule/edit/0` }, { replace: true });
        };
        if (guardNavigation?.(doNavigate)) return;
        doNavigate();
      } else {
        // No "the" — the wording differs from its five siblings and is preserved as-is.
        toast.info("You don't have permission to create rule");
      }
    };

    const createProduct = () => {
      if (appData.pm.checkPermission("gmsproducts_update")) {
        const doNavigate = () => {
          appData.reset();
          navigate({ pathname: `/gms/product/edit/0` }, { replace: true });
        };
        if (guardNavigation?.(doNavigate)) return;
        doNavigate();
      } else {
        toast.info("You don't have permission to create the product");
      }
    };

    /**
     * Guide Model, Commend Model and A/B Test: navigate to the module's list page with
     * `state.openCreate`, which the landing page reads to auto-open its create dialog.
     *
     * **These three are NOT routed through `guardNavigation`, and that is preserved
     * rather than endorsed.** The five above intercept unsaved edits; these do not, so a
     * user mid-edit loses their work with no prompt. It was true before this migration
     * too — but only these three actually navigated, so it was the *only* live path. Now
     * that all eight work, the gap is wider. Adding `guardNavigation?.(doNavigate)` here
     * is a one-line change and a behaviour change; it needs a decision, not a drive-by.
     * See `okf/templates/e2e.md` § 14.
     */
    const createViaOpenCreate = (permission: string, pathname: string, what: string) => {
      if (appData.pm.checkPermission(permission)) {
        navigate({ pathname }, { state: { openCreate: true }, replace: true });
      } else {
        toast.info(`You don't have permission to create the ${what}`);
      }
    };

    const handleSelect = (optionId: string | number) => {
      switch (optionId) {
        case 'category-type':
          return createCategory('CT', '/gms/categories/ct', 'category type');
        case 'category':
          return createCategory('C', '/gms/categories/c', 'category');
        case 'attribute':
          return navigateToAttributeForm();
        case 'rule':
          return createRule();
        case 'product':
          return createProduct();
        case 'guide-model':
          // Item was renamed to "Guide Model" but still pointed at the legacy
          // `/gms/builder/edit/0` (Smart Builder) route. The Guide module is mounted at
          // `/gms/guide` and reads `state.openCreate` in ISRHomePage to auto-open the
          // create dialog. Permission is `gmsguide_create` (ISRProjectTable.tsx:42).
          return createViaOpenCreate('gmsguide_create', '/gms/guide', 'guide model');
        case 'commend-model':
          return createViaOpenCreate('recommender_create', '/gms/commend', 'commend model');
        case 'ab-test':
          return createViaOpenCreate('gmsabtesting_create', '/gms/ab', 'A/B test');
        default:
          return undefined;
      }
    };

    return (
      <div id={id} role="group">
        <DropdownButton
          data-testid="create-btn"
          label="Create"
          options={CREATE_OPTIONS}
          onSelect={handleSelect}
          /* `secondary` (user-called 2026-09-01). The fill era's own comment ended
             "one word to change if it reads too loud beside a page's own primary" —
             it did: a saturated block permanently parked in the chrome competed
             with every page's actual primary action below it. The outline keeps
             Create discoverable (label + chevron on the control grid) while the
             chrome stays quiet. Note DropdownButton.secondary is INK-labelled,
             unlike Button.secondary's accent label (§ 31 border-tier decision). */
          variant="secondary"
          /* Eight items at the default 36px row is 288px of menu hanging off a 60px
             header. `compact` is 28px — the density the Bootstrap `.dropdown-item` it
             replaced had (~29px), and still clear of SC 2.5.8. */
          menuVariant="compact"
        />
      </div>
    );
}

export default SearchBarCreateButton
